import { useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import apiFetch from '../../../utils/apiFetch';
import { notify } from '../../../utils/notification';

// Fetch privately after invalidation; shared socket events never carry objective content.
export default function useObjectiveNotifications(campaignId, characterId) {
  const [status, setStatus] = useState({ scope: '', count: 0 });
  const scope = `${campaignId || ''}:${characterId || ''}`;
  useEffect(() => {
    if (!campaignId || !characterId) return undefined;
    let live = true, sequence = 0;
    const announced = new Set();
    const refresh = async () => {
      const request = ++sequence;
      try {
        const response = await apiFetch(`/campaigns/${encodeURIComponent(campaignId)}/objectives?characterId=${encodeURIComponent(characterId)}`, { cache: 'no-store' });
        if (!response.ok) {
          if (live && request === sequence && [401, 403].includes(response.status)) setStatus({ scope, count: 0 });
          return;
        }
        const data = await response.json();
        if (!live || request !== sequence) return;
        if (data.isRecipient === false) { setStatus({ scope, count: 0 }); return; }
        const unread = (data.objectives || []).filter(objective => objective.unread);
        setStatus({ scope, count: unread.length });
        const unreadIds = new Set(unread.map(objective => objective.id));
        announced.forEach(id => { if (!unreadIds.has(id)) announced.delete(id); });
        const unseen = unread.filter(objective => !announced.has(objective.id));
        unread.forEach(objective => announced.add(objective.id));
        if (unseen.length) notify(unseen.length === 1
          ? `New objective from your DM: ${unseen[0].title}`
          : `${unseen.length} unread objectives from your DM. Open Objectives and view their details.`, 'info');
      } catch { /* Preserve the unread glow while offline; reconnect will refresh. */ }
    };
    const socket = io(process.env.REACT_APP_API_URL || undefined, { withCredentials: true });
    socket.on('connect', () => socket.emit('campaign:join', campaignId, result => {
      if (result?.status === 'joined') refresh();
    }));
    socket.on('campaign:objectives:update', refresh);
    const changed = event => {
      if (event.detail?.campaignId === campaignId && String(event.detail?.characterId) === String(characterId)) refresh();
    };
    window.addEventListener('realmtracker:objectives-changed', changed);
    window.addEventListener('focus', refresh);
    refresh();
    return () => {
      live = false; sequence++;
      socket.disconnect();
      window.removeEventListener('realmtracker:objectives-changed', changed);
      window.removeEventListener('focus', refresh);
    };
  }, [campaignId, characterId, scope]);
  return status.scope === scope ? status.count : 0;
}
